import { CSSProperties } from "react";
import { JobReadinessJobRow } from "types/jobReadiness";
import {
  formatMoney,
  formatChecklistStatus,
  formatDateLabel,
  formatQty,
  getChecklistPillStyle,
  getConfirmedPillStyle,
  groupBySource,
} from "./dispatchReadinessTableUtils";

export default function DispatchReadinessTable({
  jobs,
  emptyText,
  onOpenChecklist,
  onOpenWorkOrder,
  thStyle,
  tdStyle,
}: {
  jobs: JobReadinessJobRow[];
  emptyText: string;
  onOpenChecklist: (jobPhaseId: string) => void;
  onOpenWorkOrder: (jobPhaseId: string) => void;
  thStyle: CSSProperties;
  tdStyle: CSSProperties;
}) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "12%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "22%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "12%" }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle}>Scheduled</th>
          <th style={thStyle}>Job</th>
          <th style={thStyle}>Checklist</th>
          <th style={thStyle}>Confirmed</th>
          <th style={thStyle}>Plant / Material</th>
          <th style={thStyle}>Material | Trucks</th>
          <th style={{ ...thStyle, textAlign: "right" }}>Revenue / Profit</th>
        </tr>
      </thead>
      <tbody>
        {jobs.length === 0 ? (
          <tr>
            <td style={tdStyle} colSpan={7}>
              {emptyText}
            </td>
          </tr>
        ) : (
          jobs.map((job) => {
            const grouped = groupBySource(job.materialLines);

            return (
              <tr key={job.key}>
                <td style={tdStyle}>{formatDateLabel(job.scheduledDate)}</td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 700 }}>
                    <button
                      type="button"
                      onClick={() => onOpenWorkOrder(job.jobPhaseId)}
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        color: "#1a73e8",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {job.jobExternalId || `Phase ${job.jobPhaseId}`}
                    </button>
                  </div>
                  <div style={{ color: "#6b778c", fontSize: 12 }}>{job.jobName || "Unnamed job"}</div>
                </td>
                <td style={tdStyle}>
                  <button
                    type="button"
                    onClick={() => onOpenChecklist(job.jobPhaseId)}
                    style={{
                      border: "none",
                      ...getChecklistPillStyle(job.checklistTotal, job.checklistOutstanding),
                      padding: "4px 10px",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {formatChecklistStatus(job.checklistTotal, job.checklistOutstanding)}
                  </button>
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      ...getConfirmedPillStyle(job.confirmedWithClient),
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {job.confirmedWithClient ? "Yes" : "No"}
                  </span>
                </td>
                <td style={tdStyle}>
                  {job.materialLines.length === 0
                    ? "No material lines"
                    : Object.entries(grouped).map(([source, lines]) => (
                        <div
                          key={`${job.key}-${source}`}
                          style={{
                            position: "relative",
                            paddingTop: 18,
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            {source}
                          </div>
                          {lines.map((line, index) => (
                            <div key={`${job.key}-${source}-${index}`} style={{ fontSize: 12 }}>
                              {line.materialName}
                            </div>
                          ))}
                        </div>
                      ))}
                </td>
                <td style={tdStyle}>
                  {job.materialLines.length === 0
                    ? "-"
                    : Object.entries(grouped).map(([source, lines]) => (
                        <div
                          key={`${job.key}-${source}-units`}
                          style={{ paddingTop: 18, marginBottom: 6 }}
                        >
                          {lines.map((line, index) => (
                            <div key={`${job.key}-${source}-units-${index}`} style={{ fontSize: 12 }}>
                              {formatQty(line.quantity)} {line.unitAbbr} | {line.trucks}
                            </div>
                          ))}
                        </div>
                      ))}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <div>{formatMoney(job.revenueScheduled)}</div>
                  <div style={{ color: "#6b778c", fontSize: 12 }}>{formatMoney(job.profitScheduled)}</div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}
